import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";
import {meService} from "@/src/modules/core/services/me";

import {employeeHrApi} from "../api";
import type {
  DisciplinaryActionPayload,
  DisciplinaryHistoryItem,
  IncidentCreatePayload,
  IncidentHistoryItem,
  OperationalHistoryItem,
  PerformanceHistoryItem,
  PerformanceReviewCreatePayload,
  RecordActionRequest,
  RecordActionResponse,
  RecordDomain,
  TrainingHistoryItem,
  TrainingRecordCreatePayload,
  LeavePoliciesPayload,
  LeavePolicy,
  LeavePolicyUpdatePayload,
  LeaveRequest,
  LeaveRequestPayload,
  LeaveRequestSubmitResponse,
  OfficerContact,
  OfficerContactPayload,
  CreateOfficerProfileRequest,
  OfficerProfile,
  LicenseComplianceRow,
  SecurityLicense,
  SecurityLicensePayload,
  SecurityLicenseReportItem,
  SecurityLicenseType,
  SecurityLicenseUpdatePayload,
  LiftWorkAuthorizationPayload,
  ReactivateOfficerProfileRequest,
  TerminateOfficerProfileRequest,
  UpdateOfficerProfileRequest,
  WorkAuthorization,
  WorkAuthorizationPayload,
  WorkEligibility,
} from "../types";

function readListData<T>(responseData: unknown): GetListResponse<T> {
  const body = responseData as
    | GetListResponse<T>
    | {data?: GetListResponse<T>}
    | {data?: T[]; meta?: GetListResponse<T>["meta"]};

  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    Array.isArray((body as {data?: unknown}).data)
  ) {
    return {
      data: (body as {data: T[]}).data,
      meta: (body as {meta?: GetListResponse<T>["meta"]}).meta ?? {total: 0},
    };
  }

  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    body.data &&
    typeof body.data === "object" &&
    Array.isArray((body.data as {data?: unknown[]}).data)
  ) {
    const nested = body.data as GetListResponse<T>;
    return {data: nested.data, meta: nested.meta ?? {total: nested.data.length}};
  }

  return {data: [], meta: {total: 0}};
}

function readOperationalHistoryRecord(responseData: unknown): OperationalHistoryItem {
  const body = responseData as {data?: OperationalHistoryItem} | OperationalHistoryItem;

  if (body && typeof body === "object" && "data" in body && body.data) {
    return body.data;
  }

  return body as OperationalHistoryItem;
}

function readOperationalHistoryList(
  responseData: unknown,
  options: ParamsOptions = {},
): GetListResponse<OperationalHistoryItem> {
  const parsed = readListData<OperationalHistoryItem>(responseData);
  const total = parsed.meta?.total ?? 0;

  if (total > 0) {
    return parsed;
  }

  const limit = options.page?.limit ?? 0;
  const offset = options.page?.offset ?? 0;
  const count = parsed.data.length;

  if (count === 0) {
    return parsed;
  }

  if (limit > 0 && count < limit) {
    return {data: parsed.data, meta: {total: offset + count}};
  }

  if (limit > 0 && count > limit && offset === 0) {
    return {data: parsed.data, meta: {total: count}};
  }

  if (limit > 0 && count === limit) {
    return {data: parsed.data, meta: {total: offset + count + 1}};
  }

  return {data: parsed.data, meta: {total: count}};
}

function readShiftsNestedList<T>(responseData: unknown, key: string): T[] {
  const body = responseData as {data?: Record<string, T[]>};
  const nested = body?.data?.[key];
  return Array.isArray(nested) ? nested : [];
}

class EmployeeHrService {
  async getSelfProfile() {
    const {data} = await employeeHrApi.getSelfProfile();
    return data.data;
  }

  async getSelfOperationalHistory(options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getSelfOperationalHistory(options);
    return readOperationalHistoryList(data, options);
  }

  async getSelfOperationalHistoryRecord(recordId: number) {
    const {data} = await employeeHrApi.getSelfOperationalHistoryRecord(recordId);
    return readOperationalHistoryRecord(data);
  }

  async getOfficerOperationalHistory(officerProfileId: number, options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getOfficerOperationalHistory(officerProfileId, options);
    return readOperationalHistoryList(data, options);
  }

  async getOfficerOperationalHistoryRecord(officerProfileId: number, recordId: number) {
    const {data} = await employeeHrApi.getOfficerOperationalHistoryRecord(
      officerProfileId,
      recordId,
    );
    return readOperationalHistoryRecord(data);
  }

  async postSelfRecordAction(domain: RecordDomain, recordId: number, body: RecordActionRequest) {
    const {data} = await employeeHrApi.postSelfRecordAction(domain, recordId, body);
    return data.data as RecordActionResponse;
  }

  async postOfficerRecordAction(
    officerProfileId: number,
    domain: RecordDomain,
    recordId: number,
    body: RecordActionRequest,
  ) {
    const {data} = await employeeHrApi.postOfficerRecordAction(
      officerProfileId,
      domain,
      recordId,
      body,
    );
    return data.data as RecordActionResponse;
  }

  async getDisciplinaryHistory(officerProfileId: number) {
    const {data} = await employeeHrApi.getDisciplinaryHistory(officerProfileId);
    return readShiftsNestedList<DisciplinaryHistoryItem>(data, "actions");
  }

  async getIncidentHistory(officerProfileId: number) {
    const {data} = await employeeHrApi.getIncidentHistory(officerProfileId);
    return readShiftsNestedList<IncidentHistoryItem>(data, "incidents");
  }

  async getTrainingHistory(officerProfileId: number) {
    const {data} = await employeeHrApi.getTrainingHistory(officerProfileId);
    return readShiftsNestedList<TrainingHistoryItem>(data, "records");
  }

  async getPerformanceHistory(officerProfileId: number) {
    const {data} = await employeeHrApi.getPerformanceHistory(officerProfileId);
    return readShiftsNestedList<PerformanceHistoryItem>(data, "reviews");
  }

  async createDisciplinaryAction(officerProfileId: number, payload: DisciplinaryActionPayload) {
    const {data} = await employeeHrApi.createDisciplinaryAction(officerProfileId, payload);
    return data.data;
  }

  async createOfficerIncident(officerProfileId: number, payload: IncidentCreatePayload) {
    const {data} = await employeeHrApi.createOfficerIncident(officerProfileId, payload);
    return data.data;
  }

  async createTrainingRecord(officerProfileId: number, payload: TrainingRecordCreatePayload) {
    const {data} = await employeeHrApi.createTrainingRecord(officerProfileId, payload);
    return data.data;
  }

  async createPerformanceReview(officerProfileId: number, payload: PerformanceReviewCreatePayload) {
    const {data} = await employeeHrApi.createPerformanceReview(officerProfileId, payload);
    return data.data;
  }

  async getSelfContacts() {
    const {data} = await employeeHrApi.getSelfContacts();
    return readListData<OfficerContact>(data).data;
  }

  async createSelfContact(payload: OfficerContactPayload) {
    const {data} = await employeeHrApi.createSelfContact(payload);
    return data.data;
  }

  async updateSelfContact(contactId: number, payload: Partial<OfficerContactPayload>) {
    const {data} = await employeeHrApi.updateSelfContact(contactId, payload);
    return data.data;
  }

  async deleteSelfContact(contactId: number) {
    await employeeHrApi.deleteSelfContact(contactId);
  }

  async getOfficerContacts(officerProfileId: number) {
    const {data} = await employeeHrApi.getOfficerContacts(officerProfileId);
    return readListData<OfficerContact>(data).data;
  }

  async createOfficerContact(officerProfileId: number, payload: OfficerContactPayload) {
    const {data} = await employeeHrApi.createOfficerContact(officerProfileId, payload);
    return data.data;
  }

  async updateOfficerContact(
    officerProfileId: number,
    contactId: number,
    payload: Partial<OfficerContactPayload>,
  ) {
    const {data} = await employeeHrApi.updateOfficerContact(officerProfileId, contactId, payload);
    return data.data;
  }

  async deleteOfficerContact(officerProfileId: number, contactId: number) {
    await employeeHrApi.deleteOfficerContact(officerProfileId, contactId);
  }

  async getSelfLeaveRequests(options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getSelfLeaveRequests(options);
    return readListData<LeaveRequest>(data);
  }

  async createSelfLeaveRequest(payload: LeaveRequestPayload) {
    const {data} = await employeeHrApi.createSelfLeaveRequest(payload);
    return data.data as LeaveRequestSubmitResponse;
  }

  async cancelSelfLeaveRequest(leaveRequestId: number) {
    const {data} = await employeeHrApi.cancelSelfLeaveRequest(leaveRequestId);
    return data.data;
  }

  async createOfficerLeaveRequest(officerProfileId: number, payload: LeaveRequestPayload) {
    const {data} = await employeeHrApi.createOfficerLeaveRequest(officerProfileId, payload);
    return data.data as LeaveRequestSubmitResponse;
  }

  async getLeaves(options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getLeaves(options);
    return readListData<LeaveRequest>(data);
  }

  async approveLeaveRequest(leaveRequestId: number) {
    const {data} = await employeeHrApi.approveLeaveRequest(leaveRequestId);
    return data.data;
  }

  async denyLeaveRequest(leaveRequestId: number) {
    const {data} = await employeeHrApi.denyLeaveRequest(leaveRequestId);
    return data.data;
  }

  async getOfficerProfiles(options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getOfficerProfiles(options);
    return readListData<OfficerProfile>(data);
  }

  async getOfficerProfile(officerProfileId: number) {
    const {data} = await employeeHrApi.getOfficerProfile(officerProfileId);
    return data.data;
  }

  async createOfficerProfile(payload: CreateOfficerProfileRequest) {
    const {data} = await employeeHrApi.createOfficerProfile(payload);
    return data.data;
  }

  async updateOfficerProfile(
    officerProfileId: number,
    payload: UpdateOfficerProfileRequest | Partial<OfficerProfile>,
  ) {
    const {data} = await employeeHrApi.updateOfficerProfile(officerProfileId, payload);
    return data.data;
  }

  async setOfficerTerminationDate(officerProfileId: number, terminationDate: string | null) {
    return this.updateOfficerProfile(officerProfileId, {termination_date: terminationDate});
  }

  async terminateOfficerProfile(
    officerProfileId: number,
    payload: TerminateOfficerProfileRequest,
  ) {
    const {data} = await employeeHrApi.terminateOfficerProfile(officerProfileId, payload);
    return data.data;
  }

  async reactivateOfficerProfile(
    officerProfileId: number,
    payload: ReactivateOfficerProfileRequest,
  ) {
    const {data} = await employeeHrApi.reactivateOfficerProfile(officerProfileId, payload);
    return data.data;
  }

  async getSecurityLicenses(officerProfileId: number) {
    const {data} = await employeeHrApi.getSecurityLicenses(officerProfileId);
    return readListData<SecurityLicense>(data).data;
  }

  async createSecurityLicense(officerProfileId: number, payload: SecurityLicensePayload) {
    const {data} = await employeeHrApi.createSecurityLicense(officerProfileId, payload);
    return data.data;
  }

  async updateSecurityLicense(
    officerProfileId: number,
    licenseId: number,
    payload: SecurityLicenseUpdatePayload,
  ) {
    const {data} = await employeeHrApi.updateSecurityLicense(
      officerProfileId,
      licenseId,
      payload,
    );
    return data.data;
  }

  async getSecurityLicenseTypes() {
    const {data} = await employeeHrApi.getSecurityLicenseTypes();
    return readListData<SecurityLicenseType>(data).data;
  }

  async getOfficerWorkEligibility(officerProfileId: number) {
    const {data} = await employeeHrApi.getOfficerWorkEligibility(officerProfileId);
    return data.data;
  }

  async getSelfWorkEligibility() {
    const {data} = await employeeHrApi.getSelfWorkEligibility();
    return data.data;
  }

  async getOfficerWorkAuthorizations(officerProfileId: number) {
    const {data} = await employeeHrApi.getOfficerWorkAuthorizations(officerProfileId);
    return readListData<WorkAuthorization>(data).data;
  }

  async createOfficerWorkAuthorization(
    officerProfileId: number,
    payload: WorkAuthorizationPayload,
  ) {
    const {data} = await employeeHrApi.createOfficerWorkAuthorization(officerProfileId, payload);
    return data.data;
  }

  async liftOfficerWorkAuthorization(
    officerProfileId: number,
    authorizationId: number,
    payload: LiftWorkAuthorizationPayload,
  ) {
    const {data} = await employeeHrApi.liftOfficerWorkAuthorization(
      officerProfileId,
      authorizationId,
      payload,
    );
    return data.data;
  }

  async getLicenseCompliance(options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getLicenseCompliance(options);
    const response = readListData<LicenseComplianceRow>(data);
    const meta = response.meta ?? {total: 0};

    return {
      ...response,
      data: response.data.map((row) => ({
        ...row,
        id: row.id ?? row.employee_profile_id,
      })),
      meta: {
        ...meta,
        total: meta.total ?? 0,
        from: meta.from ?? 0,
        to: meta.to ?? 0,
      },
    };
  }

  async getExpiringSecurityLicenses(options: ParamsOptions = {}) {
    const {data} = await employeeHrApi.getExpiringSecurityLicenses(options);
    return readListData<SecurityLicenseReportItem>(data);
  }

  async getCompanyLeavePolicies(companyId: number) {
    const {data} = await employeeHrApi.getCompanyLeavePolicies(companyId);
    return data.data ?? [];
  }

  async upsertCompanyLeavePolicies(companyId: number, payload: LeavePoliciesPayload) {
    const {data} = await employeeHrApi.upsertCompanyLeavePolicies(companyId, payload);
    return data.data as LeavePolicy[];
  }

  async updateCompanyLeavePolicy(
    companyId: number,
    policyId: number,
    payload: LeavePolicyUpdatePayload,
  ) {
    const {data} = await employeeHrApi.updateCompanyLeavePolicy(companyId, policyId, payload);
    return data.data as LeavePolicy;
  }

  getIsUpdateEnabled(): boolean {
    return meService.getHasPermission("employee update");
  }

  getIsStoreEnabled(): boolean {
    return meService.getHasPermission("employee store");
  }
}

export const employeeHrService = new EmployeeHrService();
